import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Badge } from '../ui/badge';
import { Wind, AlertCircle, Info, RotateCcw, Calculator, Table2 } from 'lucide-react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, ReferenceDot,
} from 'recharts';
import {
  DEFAULT_INPUTS, FIELD_SPECS, DENSITY_VALUE_SPECS, ATMOSPHERE_SPECS, ALL_SPECS, WIND_DIRECTIONS,
  windDirectionForAngle, VEHICLE_PRESETS, AIR_DENSITY_PRESETS, validateField,
} from '../../lib/aerodynamicForceTools';
import { aerodynamicForceToolApi } from '../../lib/toolsApi';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './simulator/ReferenceLineTopLabel';

const CALC_DEBOUNCE_MS = 350;

const SPEC_BY_KEY = Object.fromEntries(ALL_SPECS.map((s) => [s.key, s]));
const DEFAULT_VEHICLE_PRESET = 'custom';
const DEFAULT_AIR_DENSITY_PRESET = 'sea_level';

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtSigned = (n, d = 1) => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${n > 0 ? '+' : ''}${fmt(n, d)}`);

const initialRaw = () => {
  const out = {};
  Object.keys(SPEC_BY_KEY).forEach((k) => { out[k] = String(DEFAULT_INPUTS[k]); });
  return out;
};

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid={`aerodynamic-force-tip-${htmlFor}`} />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const NumberField = ({ fieldKey, id, tip, raw, setField, fieldErrors, disabled = false, children }) => (
  <div className="space-y-2">
    <FieldLabel htmlFor={id} unit={SPEC_BY_KEY[fieldKey].unit} tip={tip}>{children}</FieldLabel>
    <Input
      id={id}
      type="number"
      step="any"
      value={raw[fieldKey]}
      onChange={(e) => setField(fieldKey, e.target.value)}
      disabled={disabled}
      className="font-mono"
      aria-invalid={!!fieldErrors[fieldKey]}
      data-testid={`aero-${fieldKey}-input`}
    />
    {fieldErrors[fieldKey] && (
      <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`aero-${fieldKey}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors[fieldKey]}
      </p>
    )}
  </div>
);

const ResultRow = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono font-bold text-sm">{children}</p>
  </div>
);

const Unit = ({ children }) => <span className="text-xs font-normal text-muted-foreground">{children}</span>;

const AerodynamicForceCalculator = () => {
  const [vehiclePreset, setVehiclePreset] = useState(DEFAULT_VEHICLE_PRESET);
  const [airDensityPreset, setAirDensityPreset] = useState(DEFAULT_AIR_DENSITY_PRESET);
  const [raw, setRaw] = useState(initialRaw);
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const densityMode = airDensityPreset === 'atmosphere' ? 'atmosphere' : 'value';
  const windDirection = windDirectionForAngle(raw.windAngleDeg);

  const applyVehiclePreset = (key) => {
    setVehiclePreset(key);
    const preset = VEHICLE_PRESETS.find((p) => p.key === key);
    if (preset && key !== 'custom') {
      setRaw((prev) => ({
        ...prev,
        dragCoefficient: String(preset.dragCoefficient),
        frontalAreaM2: String(preset.frontalAreaM2),
        yawSensitivity: String(preset.yawSensitivity),
      }));
    }
  };

  useEffect(() => {
    const preset = AIR_DENSITY_PRESETS.find((p) => p.key === airDensityPreset);
    if (preset && preset.value !== null) setField('airDensityKgM3', String(preset.value));
  }, [airDensityPreset]);

  const onWindDirectionChange = (value) => {
    const dir = WIND_DIRECTIONS.find((w) => w.value === value);
    if (!dir) return;
    if (dir.angle !== null) setField('windAngleDeg', String(dir.angle));
    else if (windDirection !== 'custom') setField('windAngleDeg', '45');
  };

  const activeSpecs = useMemo(
    () => [...FIELD_SPECS, ...(densityMode === 'atmosphere' ? ATMOSPHERE_SPECS : DENSITY_VALUE_SPECS)],
    [densityMode],
  );

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const spec of activeSpecs) {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [raw, activeSpecs]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  const runCalculation = () => {
    if (hasErrors) return;
    const requestId = ++requestIdRef.current;
    const payload = { ...values, airDensityMode: densityMode };
    aerodynamicForceToolApi.calculate(payload)
      .then((outcome) => {
        if (requestId !== requestIdRef.current) return;
        if (outcome.errors) {
          setResults(null);
          setCalcError(Object.values(outcome.errors)[0]);
        } else {
          // Keep the inputs the result was computed from, so the chart/tables never pair
          // a fresh input with a stale result while a debounce is pending.
          setResults({ ...outcome.results, inputs: payload });
          setCalcError(null);
        }
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setResults(null);
        setCalcError(e.message);
      });
  };

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return; }
    const timer = setTimeout(runCalculation, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values, densityMode]);

  const handleReset = () => {
    setRaw(initialRaw());
    setVehiclePreset(DEFAULT_VEHICLE_PRESET);
    setAirDensityPreset(DEFAULT_AIR_DENSITY_PRESET);
  };

  const windNote = WIND_DIRECTIONS.find((w) => w.value === windDirection)?.note;
  const fieldProps = { raw, setField, fieldErrors };
  const presetLocked = vehiclePreset !== 'custom';

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6" aria-label="Aerodynamic Force Calculator">
        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="technical-card lg:col-span-3">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <Wind className="h-5 w-5 text-autoxium-blue" />
                Aerodynamic Force Calculator
              </CardTitle>
              <CardDescription>
                Calculate the aerodynamic drag force on a vehicle and the power and energy
                needed to overcome it, for any wind speed and direction, vehicle class, and
                altitude/temperature.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 p-4 bg-muted/50 rounded-md">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Speed &amp; Wind</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <NumberField fieldKey="vehicleSpeedKmph" id="aero-speed" tip="Vehicle speed over ground." {...fieldProps}>
                    Vehicle Speed
                  </NumberField>
                  <NumberField
                    fieldKey="windSpeedKmph" id="aero-wind-speed" {...fieldProps}
                    tip="Ambient wind speed at vehicle height. Weather reports give wind at 10 m above ground; near the road surface it is typically only about 70–80% of that figure, so reduce a forecast value accordingly."
                  >
                    Wind Speed
                  </NumberField>
                </div>

                <div className="space-y-2">
                  <FieldLabel tip="Direction the wind comes from, relative to the vehicle's heading. Pick a quick direction or choose Custom Angle to enter any angle from 0° (headwind) to 180° (tailwind).">
                    Wind Direction
                  </FieldLabel>
                  <Tabs value={windDirection} onValueChange={onWindDirectionChange}>
                    <TabsList className="flex flex-wrap h-auto" data-testid="aero-wind-direction-tabs">
                      {WIND_DIRECTIONS.map((w) => (
                        <TabsTrigger key={w.value} value={w.value} data-testid={`aero-wind-direction-${w.value}`}>{w.label}</TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                  {windNote && <p className="text-xs text-muted-foreground leading-relaxed">{windNote}</p>}
                </div>
                {windDirection === 'custom' && (
                  <div className="sm:w-1/2 sm:pr-2">
                    <NumberField
                      fieldKey="windAngleDeg" id="aero-wind-angle" {...fieldProps}
                      tip="0° = wind blowing straight at the vehicle's nose (headwind), 90° = straight across (crosswind), 180° = from directly behind (tailwind)."
                    >
                      Wind Angle
                    </NumberField>
                  </div>
                )}
              </div>

              <div className="space-y-4 p-4 bg-muted/50 rounded-md">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle</p>
                <div className="space-y-2">
                  <FieldLabel tip="Preset drag coefficient, frontal area and yaw sensitivity for typical vehicle categories. Choose Custom to enter your own values.">
                    Vehicle Type
                  </FieldLabel>
                  <Select value={vehiclePreset} onValueChange={applyVehiclePreset}>
                    <SelectTrigger className="w-full sm:w-80" data-testid="aero-vehicle-preset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VEHICLE_PRESETS.map((p) => (
                        <SelectItem key={p.key} value={p.key}>
                          {p.label}{p.dragCoefficient !== null ? ` (Cd ${p.dragCoefficient}, ${p.frontalAreaM2} m²)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <NumberField
                    fieldKey="dragCoefficient" id="aero-cd" disabled={presetLocked} {...fieldProps}
                    tip="Aerodynamic drag coefficient at zero yaw (air straight on) — a dimensionless measure of how streamlined the shape is. Lower is more aerodynamic."
                  >
                    Drag Coefficient
                  </NumberField>
                  <NumberField
                    fieldKey="frontalAreaM2" id="aero-area" disabled={presetLocked} {...fieldProps}
                    tip="Projected frontal area of the vehicle — the cross-section it presents to oncoming air."
                  >
                    Frontal Area
                  </NumberField>
                  <NumberField
                    fieldKey="yawSensitivity" id="aero-yaw-k" disabled={presetLocked} {...fieldProps}
                    tip="How strongly drag rises when the air meets the vehicle at an angle (yaw), in the model Cx = Cd·(1 + k·sin²ψ)·cosψ. Streamlined cars ≈ 2–3, boxy vans/trucks/buses ≈ 3.5–5. Only matters when there is a crosswind component; 0 disables the yaw effect."
                  >
                    Yaw Sensitivity
                  </NumberField>
                </div>
                <div className="sm:w-1/3 sm:pr-3">
                  <NumberField
                    fieldKey="drivetrainEfficiencyPct" id="aero-eta" {...fieldProps}
                    tip="Efficiency from the energy source (battery or engine output) to the wheels. Typical: EV 85–92%, manual/automatic ICE driveline 85–93%. Used only for the 'at source' power and energy figures."
                  >
                    Drivetrain Efficiency
                  </NumberField>
                </div>
              </div>

              <div className="space-y-4 p-4 bg-muted/50 rounded-md">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Air</p>
                <div className="space-y-2">
                  <FieldLabel htmlFor="aero-density" unit={densityMode === 'value' ? SPEC_BY_KEY.airDensityKgM3.unit : ''} tip="Density of the surrounding air, which falls with altitude and temperature. Choose a preset, compute it from altitude & temperature, or enter a custom value.">
                    Air Density
                  </FieldLabel>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={airDensityPreset} onValueChange={setAirDensityPreset}>
                      <SelectTrigger className="w-full sm:w-72" data-testid="aero-air-density-preset-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {AIR_DENSITY_PRESETS.map((p) => (
                          <SelectItem key={p.key} value={p.key}>
                            {p.label}{p.value !== null ? ` (${p.value})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {densityMode === 'value' && (
                      <Input
                        id="aero-density"
                        type="number"
                        step="any"
                        value={raw.airDensityKgM3}
                        onChange={(e) => setField('airDensityKgM3', e.target.value)}
                        disabled={airDensityPreset !== 'custom'}
                        className="font-mono sm:max-w-[9rem]"
                        aria-invalid={!!fieldErrors.airDensityKgM3}
                        data-testid="aero-airDensityKgM3-input"
                      />
                    )}
                  </div>
                  {fieldErrors.airDensityKgM3 && (
                    <p className="flex items-center gap-1 text-xs text-destructive" data-testid="aero-airDensityKgM3-error">
                      <AlertCircle className="h-3 w-3 shrink-0" /> {fieldErrors.airDensityKgM3}
                    </p>
                  )}
                </div>
                {densityMode === 'atmosphere' && (
                  <>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <NumberField fieldKey="altitudeM" id="aero-altitude" tip="Altitude above mean sea level. Pressure follows the ISA standard atmosphere (local weather-driven pressure changes are not included)." {...fieldProps}>
                        Altitude
                      </NumberField>
                      <NumberField fieldKey="ambientTempC" id="aero-temp" tip="Actual outside air temperature at the vehicle." {...fieldProps}>
                        Temperature
                      </NumberField>
                      <NumberField fieldKey="relativeHumidityPct" id="aero-rh" tip="Relative humidity. Humid air is slightly less dense than dry air (water vapour is lighter) — the effect is usually under 1–2%." {...fieldProps}>
                        Humidity
                      </NumberField>
                    </div>
                    {results?.atmosphere && (
                      <p className="text-xs text-muted-foreground font-mono" data-testid="aero-computed-density">
                        Computed: ρ = {fmt(results.airDensityKgM3, 3)} kg/m³ · P = {fmt(results.atmosphere.pressureKpa, 1)} kPa
                        · {fmtSigned((results.densityRatio - 1) * 100, 1)}% drag vs sea-level standard
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="button" onClick={runCalculation} disabled={hasErrors} data-testid="aero-calculate-btn">
                  <Calculator className="h-4 w-4" /> Calculate
                </Button>
                <Button type="button" variant="outline" onClick={handleReset} data-testid="aero-reset-btn">
                  <RotateCcw className="h-4 w-4" /> Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <ResultsCard hasErrors={hasErrors} calcError={calcError} results={results} />
            {results && (
              <Card className="technical-card">
                <CardContent className="pt-6 space-y-2">
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="aero-interpretation">
                    <span className="font-semibold text-foreground">Aerodynamic drag force</span> grows
                    with the square of relative air speed, and the power to overcome it with the
                    cube of vehicle speed — doubling speed roughly quadruples drag force and
                    multiplies drag power by eight. That is why aerodynamic drag dominates road load
                    at highway speed even though it is small in city driving.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Vehicle Aerodynamics Diagram</CardTitle>
            <CardDescription>Updates live as Vehicle Speed, Wind Speed, and Wind Direction change. Vector lengths are illustrative, not to scale.</CardDescription>
          </CardHeader>
          <CardContent>
            <AerodynamicDiagram
              speedKmph={Number(raw.vehicleSpeedKmph) || 0}
              windSpeedKmph={Number(raw.windSpeedKmph) || 0}
              windAngleDeg={Math.min(Math.max(Number(raw.windAngleDeg) || 0, 0), 180)}
              isAssist={!!results?.isWindAssist}
            />
          </CardContent>
        </Card>

        <SpeedForceChart results={results} />

        {results && (
          <>
            <SpeedSweepTable results={results} />
            <WindScenarioTable results={results} />
            <DensityTable results={results} />
          </>
        )}
        <VehicleClassTable onUse={applyVehiclePreset} activeKey={vehiclePreset} />

        <MethodCard results={results} />
      </div>
    </TooltipProvider>
  );
};

const ResultsCard = ({ hasErrors, calcError, results }) => (
  <Card className="technical-card ring-1 ring-autoxium-blue/30 h-fit" data-testid="aero-results-card">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-base">Aerodynamic Drag</CardTitle>
      <CardDescription>Drag Force, Power &amp; Energy</CardDescription>
    </CardHeader>
    <CardContent>
      <div role="status" aria-live="polite">
        {hasErrors ? (
          <p className="text-sm text-muted-foreground" data-testid="aero-results-placeholder">
            Fix the highlighted field to see results.
          </p>
        ) : calcError ? (
          <div className="flex items-center gap-2 text-sm text-destructive" data-testid="aero-calc-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
          </div>
        ) : !results ? (
          <p className="text-sm text-muted-foreground" data-testid="aero-results-loading">
            Calculating…
          </p>
        ) : (
          <div className="space-y-4" data-testid="aero-results">
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-2">
                Aerodynamic Drag Force
                {results.isWindAssist && <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-600/40" data-testid="aero-assist-badge">Wind assist</Badge>}
              </div>
              <p className="font-mono font-black text-2xl text-autoxium-blue" data-testid="aero-force">{fmt(results.aeroForceN, 1)} <span className="text-sm font-normal text-muted-foreground">N</span></p>
              <p className="font-mono text-sm text-muted-foreground">{fmt(results.aeroForceKn, 3)} kN</p>
              {results.isWindAssist && (
                <p className="text-xs text-muted-foreground mt-1">Negative: the tailwind is faster than the vehicle and pushes it forward.</p>
              )}
            </div>
            <div className="pt-3 border-t border-structure grid grid-cols-2 gap-3">
              <ResultRow label="Power at Wheels" testId="aero-wheel-power">
                {fmt(results.wheelPowerKw, 2)} <Unit>kW</Unit>
                <span className="block text-xs font-normal text-muted-foreground">{fmt(results.wheelPowerHp, 1)} hp</span>
              </ResultRow>
              <ResultRow label="Power at Source" testId="aero-source-power">
                {fmt(results.sourcePowerKw, 2)} <Unit>kW</Unit>
                <span className="block text-xs font-normal text-muted-foreground">{fmt(results.sourcePowerHp, 1)} hp · η {fmt(results.inputs.drivetrainEfficiencyPct, 0)}%</span>
              </ResultRow>
              <ResultRow label="Energy at Wheels" testId="aero-wheel-energy">
                {fmt(results.wheelEnergyWhPerKm, 1)} <Unit>Wh/km</Unit>
              </ResultRow>
              <ResultRow label="Energy at Source" testId="aero-source-energy">
                {fmt(results.sourceEnergyWhPerKm, 1)} <Unit>Wh/km</Unit>
              </ResultRow>
            </div>
            <div className="pt-3 border-t border-structure grid grid-cols-2 gap-3">
              <ResultRow label="Relative Air Speed" testId="aero-relative-speed">
                {fmt(results.relativeAirSpeedKmph, 1)} <Unit>km/h</Unit>
              </ResultRow>
              <ResultRow label="Yaw Angle" testId="aero-yaw">
                {fmt(results.yawAngleDeg, 1)} <Unit>°</Unit>
              </ResultRow>
              <ResultRow label="Still-Air Drag" testId="aero-still-air">
                {fmt(results.dragForceAtVehicleSpeedN, 1)} <Unit>N</Unit>
              </ResultRow>
              <ResultRow label="Wind Effect" testId="aero-wind-effect">
                {results.windChangePercent === null ? (
                  <span className="text-muted-foreground" title="Undefined when the vehicle is stationary (no still-air baseline).">n/a</span>
                ) : (
                  <span className={results.windChangePercent > 0 ? 'text-destructive' : results.windChangePercent < 0 ? 'text-emerald-600' : ''}>
                    {fmtSigned(results.windChangePercent, 1)}<Unit> %</Unit>
                  </span>
                )}
              </ResultRow>
              <ResultRow label="Drag Area Cd·A" testId="aero-cda">
                {fmt(results.dragAreaM2, 3)} <Unit>m²</Unit>
              </ResultRow>
              <ResultRow label="Air Density Used" testId="aero-density-used">
                {fmt(results.airDensityKgM3, 3)} <Unit>kg/m³</Unit>
              </ResultRow>
            </div>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const SpeedForceChart = ({ results }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const chartData = results?.chart || [];
  const speed = results?.inputs.vehicleSpeedKmph;
  const showWindLine = results ? results.inputs.windSpeedKmph > 0 : false;
  const hasNegative = chartData.some((p) => p.forceWithWind < 0);
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2">
          <Wind className="h-4 w-4 text-autoxium-blue" /> Speed vs Aerodynamic Drag Force &amp; Power
        </CardTitle>
        <CardDescription>
          Drag force (left axis) grows with the square of speed; the power needed at the source
          (right axis) grows roughly with the cube. The dashed line and dot mark the current
          operating point.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!results ? (
          <p className="text-sm text-muted-foreground py-12 text-center" data-testid="aero-chart-placeholder">
            Enter valid inputs to see the Speed vs Drag Force chart.
          </p>
        ) : (
          <>
            <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-aero" />
            <div className="h-96" data-testid="aero-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 8, left: 8, bottom: 24 }}
                  onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
                >
                  <CartesianGrid stroke={CHART_GRID} vertical={false} />
                  <XAxis
                    dataKey="speedKmph" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
                    label={{ value: 'Vehicle Speed (km/h)', position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }}
                    tickFormatter={(v) => Math.round(v).toLocaleString()}
                  />
                  <YAxis
                    yAxisId="force" tick={AXIS_TICK_STYLE} width={64}
                    label={{ value: 'Drag Force (N)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }}
                  />
                  <YAxis
                    yAxisId="power" orientation="right" tick={AXIS_TICK_STYLE} width={56}
                    label={{ value: 'Source Power (kW)', angle: 90, position: 'insideRight', style: AXIS_LABEL_STYLE }}
                  />
                  <RechartsTooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    formatter={(v, name) => [fmt(v, name.includes('kW') ? 2 : 1), name]}
                    labelFormatter={(v) => `${fmt(v, 0)} km/h`}
                  />
                  <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
                  {hasNegative && <ReferenceLine y={0} yAxisId="force" stroke={CHART_AXIS} />}
                  <ReferenceLine
                    x={speed} yAxisId="force" stroke="#dc2626" strokeDasharray="4 4"
                    label={<ReferenceLineTopLabel text="Operating Point" color="#dc2626" anchor={referenceLabelAnchorFor(speed, zoom.xDomain, chartData, 'speedKmph')} />}
                  />
                  <Line yAxisId="force" type="monotone" dataKey="forceWithWind" name={showWindLine ? 'Drag Force, with wind (N)' : 'Drag Force (N)'} stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
                  {showWindLine && (
                    <Line yAxisId="force" type="monotone" dataKey="forceNoWind" name="Drag Force, still air (N)" stroke={SERIES_COLORS[2]} strokeDasharray="5 4" {...LINE_PROPS} />
                  )}
                  <Line yAxisId="power" type="monotone" dataKey="sourcePowerKw" name="Source Power (kW)" stroke={SERIES_COLORS[1]} {...LINE_PROPS} />
                  <ReferenceDot x={speed} y={results.aeroForceN} yAxisId="force" r={5} fill="#dc2626" stroke="#fff" strokeWidth={2} ifOverflow="discard" />
                  {zoom.isSelecting && (
                    <ReferenceArea yAxisId="force" x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md p-2.5 mt-3">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          Aerodynamic drag scales with the square of speed while rolling resistance stays roughly
          constant — past a crossover speed (typically about 60–90 km/h for a passenger car,
          depending on its mass, tyres and Cd·A), overcoming air resistance becomes the largest
          part of the power a vehicle needs to hold a steady speed.
        </div>
      </CardContent>
    </Card>
  );
};

const TableCard = ({ title, description, children, testId }) => (
  <Card className="technical-card" data-testid={testId}>
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2">
        <Table2 className="h-4 w-4 text-autoxium-blue" /> {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const HEAD_ROW = 'text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent';
const TH = 'text-right h-auto py-2 pr-3 whitespace-nowrap';
const TD = 'text-right py-2 pr-3 whitespace-nowrap';

const SpeedSweepTable = ({ results }) => (
  <TableCard
    title="Drag vs Speed"
    testId="aero-speed-table"
    description="Drag force, power and energy across a range of speeds, for the current vehicle, wind and air. The highlighted row is your entered speed."
  >
    <Table className="text-xs font-mono min-w-[640px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Speed (km/h)</TableHead>
          <TableHead className={TH}>Drag Force (N)</TableHead>
          <TableHead className={TH}>Still-Air Force (N)</TableHead>
          <TableHead className={TH}>Wheel Power (kW)</TableHead>
          <TableHead className={TH}>Source Power (kW)</TableHead>
          <TableHead className={TH}>Energy at Source (Wh/km)</TableHead>
          <TableHead className="text-right h-auto py-2 whitespace-nowrap">Force vs Current</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.speedSweep.map((r) => (
          <TableRow key={r.speedKmph} className={r.isCurrent ? 'bg-autoxium-blue/5 font-bold' : ''}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{fmt(r.speedKmph, r.speedKmph % 1 ? 1 : 0)}{r.isCurrent ? ' ◂' : ''}</TableCell>
            <TableCell className={TD}>{fmt(r.forceN, 1)}</TableCell>
            <TableCell className={TD}>{fmt(r.stillAirForceN, 1)}</TableCell>
            <TableCell className={TD}>{fmt(r.wheelPowerKw, 2)}</TableCell>
            <TableCell className={TD}>{fmt(r.sourcePowerKw, 2)}</TableCell>
            <TableCell className={TD}>{r.speedKmph > 0 ? fmt(r.sourceEnergyWhPerKm, 1) : '—'}</TableCell>
            <TableCell className="text-right py-2 whitespace-nowrap">{r.forceVsCurrentPct === null ? '—' : `${fmt(r.forceVsCurrentPct, 0)}%`}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const WindScenarioTable = ({ results }) => (
  <TableCard
    title="Wind Scenarios at Current Speed"
    testId="aero-wind-table"
    description={`How common wind conditions change drag at ${fmt(results.inputs.vehicleSpeedKmph, 0)} km/h for the current vehicle and air. Angles: 0° headwind, 45° quartering head, 90° crosswind, 180° tailwind.`}
  >
    <Table className="text-xs font-mono min-w-[600px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Condition</TableHead>
          <TableHead className={TH}>Relative Air (km/h)</TableHead>
          <TableHead className={TH}>Yaw (°)</TableHead>
          <TableHead className={TH}>Drag Force (N)</TableHead>
          <TableHead className={TH}>Δ vs Calm</TableHead>
          <TableHead className="text-right h-auto py-2 whitespace-nowrap">Source Power (kW)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.windScenarios.map((r) => (
          <TableRow key={r.label}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{r.label}</TableCell>
            <TableCell className={TD}>{fmt(r.relativeAirSpeedKmph, 1)}</TableCell>
            <TableCell className={TD}>{fmt(r.yawAngleDeg, 1)}</TableCell>
            <TableCell className={TD}>{fmt(r.forceN, 1)}</TableCell>
            <TableCell className={`${TD} ${r.changeVsCalmPct > 0 ? 'text-destructive' : r.changeVsCalmPct < 0 ? 'text-emerald-600' : ''}`}>
              {r.changeVsCalmPct === null ? '—' : `${fmtSigned(r.changeVsCalmPct, 1)}%`}
            </TableCell>
            <TableCell className="text-right py-2 whitespace-nowrap">{fmt(r.sourcePowerKw, 2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const DensityTable = ({ results }) => (
  <TableCard
    title="Air Density by Altitude & Temperature"
    testId="aero-density-table"
    description="Dry-air density (ISA pressure) and the resulting change in drag versus the 1.225 kg/m³ sea-level standard. Drag is directly proportional to air density."
  >
    <Table className="text-xs font-mono min-w-[520px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Altitude (m)</TableHead>
          {results.densityTable.tempsC.map((t) => (
            <TableHead key={t} className={TH}>{t}°C — ρ (Δ drag)</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.densityTable.rows.map((row) => (
          <TableRow key={row.altitudeM}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{row.altitudeM.toLocaleString()}</TableCell>
            {row.cells.map((c) => (
              <TableCell key={c.tempC} className={TD}>
                {fmt(c.densityKgM3, 3)} <span className="text-muted-foreground">({fmtSigned(c.dragChangePct, 1)}%)</span>
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const VehicleClassTable = ({ onUse, activeKey }) => (
  <TableCard
    title="Reference Values by Vehicle Class"
    testId="aero-class-table"
    description="Typical drag coefficient, frontal area and drag area (Cd·A) for each preset. Cd·A is what actually sets drag — a large, sleek vehicle can have more drag than a small, boxy one. Representative figures, not specific model measurements."
  >
    <Table className="text-xs font-mono min-w-[560px]">
      <TableHeader>
        <TableRow className={HEAD_ROW}>
          <TableHead className="text-left h-auto py-2 pr-3">Vehicle Class</TableHead>
          <TableHead className={TH}>Cd</TableHead>
          <TableHead className={TH}>Frontal Area (m²)</TableHead>
          <TableHead className={TH}>Cd·A (m²)</TableHead>
          <TableHead className={TH}>Yaw k</TableHead>
          <TableHead className="text-right h-auto py-2" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {VEHICLE_PRESETS.filter((p) => p.key !== 'custom').map((p) => (
          <TableRow key={p.key} className={activeKey === p.key ? 'bg-autoxium-blue/5' : ''}>
            <TableCell className="py-2 pr-3 whitespace-nowrap">{p.label}</TableCell>
            <TableCell className={TD}>{fmt(p.dragCoefficient, 2)}</TableCell>
            <TableCell className={TD}>{fmt(p.frontalAreaM2, 2)}</TableCell>
            <TableCell className={TD}>{fmt(p.dragCoefficient * p.frontalAreaM2, 3)}</TableCell>
            <TableCell className={TD}>{fmt(p.yawSensitivity, 1)}</TableCell>
            <TableCell className="text-right py-1">
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => onUse(p.key)} data-testid={`aero-class-use-${p.key}`}>
                Use
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

const MethodCard = ({ results }) => (
  <Card className="technical-card" data-testid="aero-method">
    <Accordion type="single" collapsible>
      <AccordionItem value="method" className="border-none">
        <CardHeader className="py-0">
          <AccordionTrigger className="hover:no-underline py-4" data-testid="aero-method-toggle">
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Calculation Method</CardTitle>
          </AccordionTrigger>
        </CardHeader>
        <AccordionContent>
          <CardContent className="pt-0 space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 1</Badge>
              Relative air velocity in vehicle axes (θ = wind angle from dead-ahead):{' '}
              <span className="font-mono text-foreground">V_x = V + V_w·cosθ, V_y = V_w·sinθ, V_rel = √(V_x² + V_y²), ψ = atan2(V_y, V_x)</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 2</Badge>
              Yaw-dependent drag coefficient: <span className="font-mono text-foreground">C_x(ψ) = Cd·(1 + k·sin²ψ)·cosψ</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 3</Badge>
              Longitudinal drag force: <span className="font-mono text-foreground">F_aero = 0.5 × ρ × C_x(ψ) × A × V_rel²</span>
              {' '}(reduces to 0.5·ρ·Cd·A·V_rel² for a pure head/tailwind)
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">step 4</Badge>
              Power and energy: <span className="font-mono text-foreground">P_wheel = F_aero × V, P_source = P_wheel / η, E = F_aero / 3.6 Wh/km</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">air</Badge>
              From altitude h and temperature T:{' '}
              <span className="font-mono text-foreground">P = 101325·(1 − 2.25577×10⁻⁵·h)^5.25588, ρ = p_d/(287.058·T) + p_v/(461.495·T)</span>
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">where</Badge>
              ρ = air density (kg/m³), Cd = drag coefficient at zero yaw, A = frontal area (m²),
              k = yaw sensitivity, V = vehicle speed (m/s), V_w = wind speed (m/s), ψ = yaw angle,
              η = drivetrain efficiency, p_d / p_v = dry-air / water-vapour partial pressure (Pa),
              T in kelvin.
            </div>
            {results && (
              <div className="pt-1 font-mono text-[11px] bg-muted/50 rounded-md px-3 py-2 break-all">
                V_rel = {fmt(results.relativeAirSpeedKmph, 1)} km/h ({fmt(results.relativeAirSpeedKmph / 3.6, 2)} m/s), ψ = {fmt(results.yawAngleDeg, 1)}°
                &nbsp;·&nbsp; C_x = {fmt(results.effectiveCx, 4)}
                &nbsp;·&nbsp; F_aero = 0.5 × {fmt(results.airDensityKgM3, 3)} × {fmt(results.effectiveCx, 4)} × {fmt(results.inputs.frontalAreaM2, 2)} × ({fmt(results.relativeAirSpeedKmph / 3.6, 2)})² = {fmt(results.aeroForceN, 1)} N
                &nbsp;·&nbsp; P_wheel = {fmt(results.aeroForceN, 1)} × {fmt(results.inputs.vehicleSpeedKmph / 3.6, 2)} = {fmt(results.wheelPowerKw * 1000, 0)} W
                &nbsp;·&nbsp; P_source = {fmt(results.wheelPowerKw * 1000, 0)} / {fmt(results.inputs.drivetrainEfficiencyPct / 100, 2)} = {fmt(results.sourcePowerKw * 1000, 0)} W
              </div>
            )}
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">note</Badge>
              The yaw model is a simplified empirical fit, not a standard: it reproduces the usual
              rise of drag with small yaw angles (roughly +5–15% at 10° for cars), goes to zero
              longitudinal drag for a pure crosswind on a stationary vehicle, and turns negative
              (wind assist) when a tailwind is faster than the vehicle. Where wind-tunnel yaw data
              or a wind-averaged drag coefficient (SAE J1252) is available, prefer it. Side force,
              lift and yaw moment are not calculated.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">note</Badge>
              Enter the wind speed at vehicle height. Weather data is measured at 10 m above
              ground, where wind is typically 25–40% stronger than near the road surface.
            </div>
          </CardContent>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </Card>
);

// ---------------------------------------------------------------------------
// Vehicle aerodynamics diagram (pure SVG, built as a markup string and injected via
// dangerouslySetInnerHTML). This project's dev-only "visual edits" Babel plugin
// (frontend/plugins/visual-edits/babel-metadata-plugin.js) wraps every dynamic JSX
// child of a lowercase element in an HTML <span data-ve-dynamic> — invalid inside the
// SVG namespace, so hand-authored JSX <svg> children (conditional arrows/labels)
// silently fail to render. Building the SVG as a string sidesteps this: every
// interpolated value here is a computed number or a hardcoded label/color string —
// never user-supplied text.
// ---------------------------------------------------------------------------

const MAX_DIAGRAM_SPEED = 250;
const n2 = (v) => v.toFixed(2);

const windLabelForAngle = (angle) => {
  if (angle === 0) return 'Headwind';
  if (angle === 90) return 'Crosswind';
  if (angle === 180) return 'Tailwind';
  return `Wind ${Math.round(angle)}°`;
};

const AerodynamicDiagram = ({ speedKmph, windSpeedKmph, windAngleDeg, isAssist }) => {
  const groundY = 170;
  const vehicleX = 200;
  const vehicleY = groundY - 17;

  // dragLen's minimum (65) is chosen so the arrow end clears the vehicle silhouette
  // (half-width ~28) even at low speed — the arrow and its label originate at the
  // vehicle body and point away from it, never crossing back through it. The arrow points
  // rearward for drag and forward when a strong tailwind turns the force into an assist.
  const speedMag = Math.max(0, Math.min(speedKmph, MAX_DIAGRAM_SPEED)) / MAX_DIAGRAM_SPEED;
  const dragLen = 65 + 65 * speedMag;
  const showDrag = speedKmph > 0.5;

  const windMag = Math.min(windSpeedKmph, MAX_DIAGRAM_SPEED) / MAX_DIAGRAM_SPEED;
  const airflowLen = 18 + 40 * windMag;
  const showAirflow = windSpeedKmph > 0.5;

  // Air moves toward -x (rearward) for a headwind and toward +y (downward on screen) for a
  // crosswind, so its screen direction is (-cosθ, +sinθ). The arrows are drawn upstream of
  // the vehicle, on an ellipse around it, flowing toward it.
  let airflowMarkup = '';
  let airflowLabel = 'No Wind';
  if (showAirflow) {
    const theta = (windAngleDeg * Math.PI) / 180;
    const dirX = -Math.cos(theta);
    const dirY = Math.sin(theta);
    const cx = vehicleX + 135 * Math.cos(theta);
    const cy = vehicleY - 50 - 60 * Math.sin(theta);
    const perpX = -dirY;
    const perpY = dirX;
    airflowMarkup = [-22, 0, 22].map((off) => {
      const mx = cx + perpX * off;
      const my = cy + perpY * off;
      const x1 = mx - (dirX * airflowLen) / 2;
      const y1 = my - (dirY * airflowLen) / 2;
      const x2 = mx + (dirX * airflowLen) / 2;
      const y2 = my + (dirY * airflowLen) / 2;
      return `<line x1="${n2(x1)}" y1="${n2(y1)}" x2="${n2(x2)}" y2="${n2(y2)}" stroke="#0891b2" stroke-width="2" marker-end="url(#aero-arrow-wind)" />`;
    }).join('');
    airflowLabel = `Airflow (${windLabelForAngle(windAngleDeg)})`;
  }

  let dragMarkup = '';
  if (showDrag) {
    if (isAssist) {
      const startX = vehicleX + 30;
      dragMarkup = `<line x1="${n2(startX)}" y1="${n2(vehicleY)}" x2="${n2(startX + dragLen * 0.6)}" y2="${n2(vehicleY)}" stroke="#059669" stroke-width="3" marker-end="url(#aero-arrow-assist)" />
        <text x="${n2(startX + 4)}" y="${n2(vehicleY - 8)}" text-anchor="start" font-size="11" font-family="monospace" font-weight="700" fill="#059669">Wind Assist Force</text>`;
    } else {
      const startX = vehicleX - 30;
      dragMarkup = `<line x1="${n2(startX)}" y1="${n2(vehicleY)}" x2="${n2(startX - dragLen * 0.8)}" y2="${n2(vehicleY)}" stroke="#dc2626" stroke-width="3" marker-end="url(#aero-arrow-drag)" />
        <text x="${n2(startX - 4)}" y="${n2(vehicleY - 8)}" text-anchor="end" font-size="11" font-family="monospace" font-weight="700" fill="#dc2626">Aerodynamic Drag Force</text>`;
    }
  }

  const svgMarkup = `
    <svg viewBox="0 0 400 200" class="w-full h-auto" role="img" aria-label="Vehicle moving at ${speedKmph.toFixed(0)} km/h with a ${windSpeedKmph.toFixed(0)} km/h wind at ${windAngleDeg.toFixed(0)} degrees">
      <defs>
        <marker id="aero-arrow-drag" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#dc2626" /></marker>
        <marker id="aero-arrow-assist" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#059669" /></marker>
        <marker id="aero-arrow-motion" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#1E40AF" /></marker>
        <marker id="aero-arrow-wind" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3.5 L0,7 Z" fill="#0891b2" /></marker>
      </defs>
      <line x1="20" y1="${groundY}" x2="380" y2="${groundY}" stroke="#94a3b8" stroke-width="2" />
      ${airflowMarkup}
      <text x="200" y="11" text-anchor="middle" font-size="11" font-family="monospace" fill="#0891b2">${airflowLabel}</text>
      <g transform="translate(${vehicleX} ${vehicleY})">
        <rect x="-8" y="-19" width="24" height="10" rx="2" fill="#1E40AF" />
        <rect x="-28" y="-9" width="56" height="14" rx="4" fill="#1E40AF" />
        <circle cx="-16" cy="5" r="6" fill="#1e293b" />
        <circle cx="16" cy="5" r="6" fill="#1e293b" />
      </g>
      <line x1="${vehicleX - 38}" y1="${vehicleY + 22}" x2="${vehicleX + 38}" y2="${vehicleY + 22}" stroke="#1E40AF" stroke-width="2" marker-end="url(#aero-arrow-motion)" />
      <text x="${vehicleX}" y="${vehicleY + 36}" text-anchor="middle" font-size="10" font-family="monospace" fill="#1E40AF">Vehicle Motion</text>
      ${dragMarkup}
    </svg>
  `;

  return (
    <div
      className="w-full max-w-xl mx-auto"
      data-testid="aero-diagram"
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
    />
  );
};

export default AerodynamicForceCalculator;
